import { http } from "@/lib/api";

// ===== Templates =====

export interface TemplateListItem {
  id: string;
  name: string;
  code: string | null;
  score: string;
  is_active: boolean;
  description: string | null;
  items_count: number;
  levels_count: number;
}

export interface TemplateEntry {
  id: string;
  name: string;
  description: string | null;
  score: string;
  order_num: number;
}

export interface TemplateItem {
  id: string;
  code: string | null;
  name: string;
  group_name: string | null;
  group_order: number;
  description: string | null;
  score: string;
  order_num: number;
  entries: TemplateEntry[];
}

export interface TemplateLevel {
  id: string;
  name: string;
  description: string | null;
  score: string;
  percentage: string | null;
  item_id: string | null;
  entry_id: string | null;
}

export interface TemplateDetail {
  id: string;
  name: string;
  code: string | null;
  score: string;
  is_active: boolean;
  description: string | null;
  items: TemplateItem[];
  levels: TemplateLevel[];
}

export function listTemplates(params: { page?: number; page_size?: number; q?: string }) {
  return http<{ items: TemplateListItem[]; total: number; page: number; page_size: number }>(
    "/templates",
    { query: params },
  );
}

export const getTemplate = (id: string) => http<TemplateDetail>(`/templates/${id}`);

export const createTemplate = (body: {
  name: string; code?: string; score?: number; description?: string; is_active?: boolean;
}) => http<TemplateDetail>("/templates", { method: "POST", body });

export const updateTemplate = (id: string, body: Partial<{
  name: string; code: string; score: number; description: string; is_active: boolean;
}>) => http<TemplateDetail>(`/templates/${id}`, { method: "PATCH", body });

export const deleteTemplate = (id: string) =>
  http<void>(`/templates/${id}`, { method: "DELETE" });

export const addItem = (tid: string, body: {
  name: string; code?: string; group_name?: string; group_order?: number;
  description?: string; score?: number; order_num?: number;
}) => http<TemplateItem>(`/templates/${tid}/items`, { method: "POST", body });

export const deleteItem = (iid: string) =>
  http<void>(`/templates/items/${iid}`, { method: "DELETE" });

export const addEntry = (iid: string, body: {
  name: string; description?: string; score?: number; order_num?: number;
}) => http<TemplateEntry>(`/templates/items/${iid}/entries`, { method: "POST", body });

export const deleteEntry = (eid: string) =>
  http<void>(`/templates/entries/${eid}`, { method: "DELETE" });

export const addLevel = (tid: string, body: {
  name: string; score: number; description?: string; percentage?: number;
  item_id?: string; entry_id?: string;
}) => http<TemplateLevel>(`/templates/${tid}/levels`, { method: "POST", body });

export const deleteLevel = (lid: string) =>
  http<void>(`/templates/levels/${lid}`, { method: "DELETE" });


// ===== Guide Fields(P1.4 动态字段配置,对齐 routers/guide_fields.py)=====

export type GuideFieldType = "text" | "textarea" | "date" | "select" | "number";
export type GuideFieldStage = "Register" | "Sharing";

export interface GuideFieldOption {
  value: string;
  label?: string | null;
  label_en?: string | null;
}

export interface GuideFieldOut {
  id: string;
  field: string;
  label: string;
  label_en: string | null;
  group_name: string | null;
  field_type: GuideFieldType;
  required: boolean;
  default_val: string | null;
  options: GuideFieldOption[] | null;
  order_num: number;
  stage: GuideFieldStage;
  is_active: boolean;
}

export type GuideFieldIn = Omit<GuideFieldOut, "id">;

/** 公开:仅启用字段,可按 stage 过滤(门户表单/详情渲染用) */
export function listGuideFields(params: { stage?: GuideFieldStage } = {}) {
  return http<GuideFieldOut[]>("/guide-fields", { query: params });
}

/** 管理:全部(含停用) */
export const listGuideFieldsAdmin = () => http<GuideFieldOut[]>("/guide-fields/admin");

export const createGuideField = (body: GuideFieldIn) =>
  http<GuideFieldOut>("/guide-fields", { method: "POST", body });

/** field key 创建后不可改(存量 extra_fields 值以它为 JSONB key) */
export const updateGuideField = (id: string, body: Partial<Omit<GuideFieldIn, "field">>) =>
  http<GuideFieldOut>(`/guide-fields/${id}`, { method: "PATCH", body });

export const deleteGuideField = (id: string) =>
  http<void>(`/guide-fields/${id}`, { method: "DELETE" });


// ===== Evaluations =====

export interface EvaluationEntryOut {
  id: string;
  item_id: string;
  entry_id: string;
  score: string | null;
  level: string | null;
  comment: string | null;
}

export interface EvaluationOut {
  id: string;
  guide_id: string;
  template_id: string;
  expert_id: string;
  status: "Waiting" | "Apply" | "Rejected" | "AIReviewed";
  stage: "First" | "Rectification" | "Final";
  score: string | null;
  level: string | null;
  review_comment: string | null;
  submitted_at: string | null;
  entries: EvaluationEntryOut[];
  // guide 级分歧标记(P1.5):已提交评审总分 max-min,>= 3 判分歧(旧站组长复评阈值)
  diff_score: string | null;
  is_divergent: boolean;
}

export interface ExpertOut {
  id: string;
  username: string;
  real_name: string | null;
}

export const listEvaluationsByGuide = (guideId: string) =>
  http<EvaluationOut[]>(`/evaluations`, { query: { guide_id: guideId } });

export const listExperts = () => http<ExpertOut[]>(`/evaluations/experts/list`);

export const createEvaluation = (body: { guide_id: string; expert_id: string; template_id: string }) =>
  http<EvaluationOut>(`/evaluations`, { method: "POST", body });

export const upsertEvaluationEntries = (
  eid: string,
  body: Array<{ item_id: string; entry_id: string; score: number; comment?: string }>,
) => http<EvaluationOut>(`/evaluations/${eid}/entries`, { method: "PUT", body });

export const submitEvaluation = (eid: string) =>
  http<EvaluationOut>(`/evaluations/${eid}/submit`, { method: "POST" });

export const deleteEvaluation = (eid: string) =>
  http<void>(`/evaluations/${eid}`, { method: "DELETE" });


// ===== Users =====

export interface UserOut {
  id: string;
  username: string;
  email: string | null;
  phone: string | null;
  real_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
  last_login_at: string | null;
  roles: string[];
}
export interface UserListOut {
  items: UserOut[];
  total: number;
  page: number;
  page_size: number;
}

export function listUsers(params: {
  page?: number; page_size?: number; q?: string; role?: string; is_active?: boolean;
} = {}) {
  return http<UserListOut>("/admin/users", { query: params });
}

export const createUser = (body: {
  username: string; password: string; email?: string; phone?: string;
  real_name?: string; role_codes: string[];
}) => http<UserOut>("/admin/users", { method: "POST", body });

export const updateUser = (uid: string, body: Partial<{
  email: string; phone: string; real_name: string; avatar_url: string;
  is_active: boolean; role_codes: string[];
}>) => http<UserOut>(`/admin/users/${uid}`, { method: "PATCH", body });

export const resetUserPassword = (uid: string, password: string) =>
  http<void>(`/admin/users/${uid}/password`, { method: "POST", body: { password } });

export const activateUser = (uid: string) =>
  http<void>(`/admin/users/${uid}/activate`, { method: "POST" });

export const deactivateUser = (uid: string) =>
  http<void>(`/admin/users/${uid}/deactivate`, { method: "POST" });

export const deleteUser = (uid: string) =>
  http<void>(`/admin/users/${uid}`, { method: "DELETE" });


// ===== Roles & Permissions =====

export interface RoleOut {
  id: string;
  code: string;
  name: string;
  description: string | null;
  permissions: string[];
}
export interface PermissionOut {
  id: string;
  code: string;
  name: string;
  description: string | null;
}

export const listRoles = () => http<RoleOut[]>("/admin/roles");
export const listPermissions = () => http<PermissionOut[]>("/admin/roles/_permissions");
export const createRole = (body: {
  code: string; name: string; description?: string; permission_codes: string[];
}) => http<RoleOut>("/admin/roles", { method: "POST", body });
export const updateRole = (rid: string, body: Partial<{
  name: string; description: string; permission_codes: string[];
}>) => http<RoleOut>(`/admin/roles/${rid}`, { method: "PATCH", body });
export const deleteRole = (rid: string) =>
  http<void>(`/admin/roles/${rid}`, { method: "DELETE" });


// ===== Swipers =====

export interface SwiperOut {
  id: string;
  title: string | null;
  title_en: string | null;
  content: string | null;
  content_en: string | null;
  image_url: string | null;
  link_url: string | null;
  order_num: number;
  is_active: boolean;
}

export const listSwipersPublic = () => http<SwiperOut[]>("/swipers");
export const listSwipersAdmin = () => http<SwiperOut[]>("/swipers/admin");
export const createSwiper = (body: Omit<SwiperOut, "id">) =>
  http<SwiperOut>("/swipers", { method: "POST", body });
export const updateSwiper = (sid: string, body: Omit<SwiperOut, "id">) =>
  http<SwiperOut>(`/swipers/${sid}`, { method: "PATCH", body });
export const deleteSwiper = (sid: string) =>
  http<void>(`/swipers/${sid}`, { method: "DELETE" });


// ===== AI 评审 =====

export type AIReviewStatus =
  | "queued" | "parsing" | "scoring" | "done" | "failed" | "budget_exceeded";

export interface EvidenceQuote {
  text: string;
  page: number | null;
}

export interface AIReviewDimOut {
  dim_code: string;
  score: string | null;
  confidence: string | null;
  risk_flags: string[] | null;
  reasoning_steps: string[] | null;
  evidence_quotes: EvidenceQuote[] | null;
}

export interface AIReviewRunOut {
  id: string;
  guide_id: string;
  pdf_sha256: string;
  prompt_version: string;
  model_primary: string;
  status: AIReviewStatus;
  cost_cny: string | null;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  promoted_to_human: boolean;
  // P3.7 低置信度复核:needs_review 后端现算(min confidence < 阈值)
  needs_review: boolean;
  min_confidence: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  dims: AIReviewDimOut[];
}

export interface AIReviewQueueItem {
  run_id: string;
  guide_id: string;
  guide_title: string | null;
  model_primary: string;
  finished_at: string | null;
  min_confidence: string;
  low_confidence_dims: string[];
}

export interface AIReviewResolveOut {
  run_id: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export const listAIReviewRunsByGuide = (guide_id: string, limit = 20) =>
  http<AIReviewRunOut[]>("/ai-reviews", { query: { guide_id, limit } });

export const getAIReviewRun = (run_id: string) =>
  http<AIReviewRunOut>(`/ai-reviews/${run_id}`);

export const triggerAIReview = (guide_id: string, pdf_url?: string) =>
  http<AIReviewRunOut>("/ai-reviews", { method: "POST", body: { guide_id, pdf_url } });

// P3.3:promote 现在落成一条人工评审草稿(evaluation + entries,幂等)
export const promoteAIReviewRun = (run_id: string) =>
  http<EvaluationOut>(`/ai-reviews/${run_id}/promote`, { method: "POST" });

export const listAIReviewQueue = () => http<AIReviewQueueItem[]>("/ai-reviews/review-queue");

export const resolveAIReviewRun = (run_id: string) =>
  http<AIReviewResolveOut>(`/ai-reviews/${run_id}/review-resolve`, { method: "POST" });

/** 重跑终态 run(可传 model 换备用模型,走异步入队) */
export const rerunAIReview = (run_id: string, model?: string) =>
  http<AIReviewRunOut>(`/ai-reviews/${run_id}/rerun`, {
    method: "POST",
    body: { model: model || null },
  });


// ===== AI 运营看板(P3.4,对齐 routers/ai_ops.py)=====

export interface AITaskWindow {
  runs: number;
  done: number;
  failed: number;
  success_rate: number | null;
}

export interface AICostWindow {
  calls: number;
  tokens_in: number;
  tokens_out: number;
  cost_cny: string;
}

export interface AIScopeCost extends AICostWindow {
  scope: string;
}

export interface AIOpsOverview {
  tasks: {
    by_status: Record<string, number>;
    queued: number;
    today: AITaskWindow;
    month: AITaskWindow;
    avg_duration_seconds: number | null;
  };
  cost: {
    today: AICostWindow;
    month: AICostWindow;
    by_scope: AIScopeCost[];
  };
}

export interface DailyCost {
  date: string;
  calls: number;
  tokens_in: number;
  tokens_out: number;
  cost_cny: string;
}

export interface RecentRun {
  id: string;
  guide_id: string;
  guide_title: string | null;
  status: AIReviewStatus;
  model_primary: string;
  prompt_version: string;
  cost_cny: string | null;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface BudgetOut {
  effective: Record<"per_call" | "per_day" | "per_month", string>;
  defaults: Record<"per_call" | "per_day" | "per_month", string>;
  overridden: string[];
}

export const getAIOpsOverview = () => http<AIOpsOverview>("/ai-ops/overview");

export const getAIOpsCostDaily = (days = 30) =>
  http<DailyCost[]>("/ai-ops/cost/daily", { query: { days } });

export const listAIOpsRuns = (limit = 20) =>
  http<RecentRun[]>("/ai-ops/runs", { query: { limit } });

export const getAIOpsBudget = () => http<BudgetOut>("/ai-ops/budget");

export const updateAIOpsBudget = (body: {
  per_call_cny?: number;
  per_day_cny?: number;
  per_month_cny?: number;
}) => http<BudgetOut>("/ai-ops/budget", { method: "PUT", body });


// ===== 管理数据看板(P3.5)=====

export interface AdminStats {
  guides: {
    total: number;
    by_stage: Record<string, number>;
    by_status: Record<string, number>;
  };
  evaluations: {
    total: number;
    by_status: Record<string, number>;
    submitted: number;
  };
  ai: {
    runs_total: number;
    queued: number;
    month: AICostWindow;
  };
}

export const getAdminStats = () => http<AdminStats>("/admin/stats");


export const DIM_LABELS: Record<string, string> = {
  coi: "利益冲突",
  multidisciplinary: "多学科参与",
  evidence: "证据基础",
  consensus: "共识方法",
  dissemination: "传播实施",
};

export const AI_STATUS_LABELS: Record<AIReviewStatus, string> = {
  queued: "排队中", parsing: "解析 PDF", scoring: "打分中",
  done: "已完成", failed: "失败", budget_exceeded: "预算熔断",
};

export const AI_STATUS_COLORS: Record<AIReviewStatus, string> = {
  queued: "default", parsing: "processing", scoring: "processing",
  done: "success", failed: "error", budget_exceeded: "warning",
};


// ===== 任务中心(P1.9)=====

export interface TaskItem {
  kind: "guide" | "evaluation" | "flow_log";
  ref_id: string;
  guide_id: string | null;
  title: string;
  stage: string | null;
  status: string | null;
  operation: string | null;
  comment: string | null;
  time: string;
}

export const listTodoTasks = () => http<TaskItem[]>("/tasks/todo");
export const listDoneTasks = () => http<TaskItem[]>("/tasks/done");
